import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { authApi } from '../api/auth';
import { useAuthStore } from '../store/auth';
import { Tabs } from '../components/ui/Tabs';
import { LanguageSwitcher } from '../components/LanguageSwitcher';

type Tab = 'email' | 'phone';

export function LoginPage() {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>('phone');
  const [phone, setPhone] = useState('');
  const [smsCode, setSmsCode] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [smsCooldown, setSmsCooldown] = useState(0);
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);

  const handleSendSms = async () => {
    if (!/^1[3-9]\d{9}$/.test(phone)) {
      alert(t('auth.login.phoneInvalid'));
      return;
    }
    await authApi.sendSms({ phone, scene: 'login' });
    setSmsCooldown(60);
    const timer = setInterval(() => {
      setSmsCooldown((c) => {
        if (c <= 1) { clearInterval(timer); return 0; }
        return c - 1;
      });
    }, 1000);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const resp =
        tab === 'phone'
          ? await authApi.loginPhone({ phone, smsCode })
          : await authApi.loginEmail({ email, password });
      setAuth(resp.user, resp.tokens.accessToken, resp.tokens.refreshToken);
      navigate('/calendar', { replace: true });
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      alert(err?.response?.data?.message || t('auth.login.failed'));
    } finally {
      setLoading(false);
    }
  };

  const handleWechatLogin = async () => {
    setLoading(true);
    try {
      const resp = await authApi.loginWechat({ code: 'dev-mock-code' });
      setAuth(resp.user, resp.tokens.accessToken, resp.tokens.refreshToken);
      navigate('/calendar', { replace: true });
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      alert(err?.response?.data?.message || t('auth.login.wechatFailed'));
    } finally {
      setLoading(false);
    }
  };

  const chips = t('auth.brandChips', { returnObjects: true }) as string[];

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Brand panel */}
      <div className="hidden lg:flex flex-col justify-between p-16 bg-black/25 backdrop-blur-sm border-r border-[var(--grid-line)]">
        <div className="font-display text-2xl tracking-wide">
          Day<span className="text-daynote-accent">·</span>Note
        </div>
        <div>
          <p className="text-[13px] italic tracking-[0.16em] uppercase text-daynote-accent mb-4">
            {t('landing.tagline')}
          </p>
          <h1 className="font-display text-[56px] leading-[1.05] font-medium tracking-tight mb-6">
            {t('landing.headingLine1')}<br />
            {t('landing.headingLine2Pre')}<em className="italic text-daynote-accent font-normal">{t('landing.headingLine2Accent')}</em>{t('landing.headingLine2Post')}
          </h1>
          <p className="text-daynote-muted text-[15px] leading-relaxed max-w-md">
            {t('landing.subtitle')}
          </p>
          <div className="grid grid-cols-2 gap-4 mt-12">
            {chips.map((f) => (
              <div
                key={f}
                className="text-xs text-daynote-muted px-4 py-3 bg-white/[0.03] border border-[var(--grid-line)] rounded-lg"
              >
                {f}
              </div>
            ))}
          </div>
        </div>
        <p className="text-xs text-daynote-text/40">DayNote v1.0 · 2026</p>
      </div>

      {/* Form panel */}
      <div className="flex items-center justify-center p-8 md:p-12 relative">
        <div className="absolute top-6 right-6">
          <LanguageSwitcher />
        </div>
        <div className="w-full max-w-[400px]">
          <h1 className="font-display text-4xl font-medium mb-1.5 tracking-tight">{t('auth.login.title')}</h1>
          <p className="text-[13px] text-daynote-muted mb-9">
            {t('auth.login.noAccount')}{' '}
            <Link to="/register" className="text-daynote-accent border-b border-daynote-accent pb-px">
              {t('auth.login.registerLink')}
            </Link>
          </p>

          <Tabs
            items={[
              { id: 'phone' as Tab, label: t('auth.login.tabPhone') },
              { id: 'email' as Tab, label: t('auth.login.tabEmail') },
            ]}
            value={tab}
            onChange={setTab}
          />

          {tab === 'phone' ? (
            <div className="space-y-4">
              <div>
                <label className="text-[11px] tracking-[0.1em] uppercase text-daynote-text/50 block mb-2">
                  {t('auth.login.phoneLabel')}
                </label>
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder={t('auth.login.phonePlaceholder')}
                  className="w-full bg-white/[0.04] border border-[var(--grid-line)] rounded-lg px-4 py-3.5 text-sm focus:outline-none focus:border-daynote-accent"
                />
              </div>
              <div>
                <label className="text-[11px] tracking-[0.1em] uppercase text-daynote-text/50 block mb-2">
                  {t('auth.login.smsLabel')}
                </label>
                <div className="relative">
                  <input
                    value={smsCode}
                    onChange={(e) => setSmsCode(e.target.value)}
                    placeholder={t('auth.login.smsPlaceholder')}
                    className="w-full bg-white/[0.04] border border-[var(--grid-line)] rounded-lg px-4 py-3.5 pr-24 text-sm focus:outline-none focus:border-daynote-accent"
                  />
                  <button
                    type="button"
                    disabled={smsCooldown > 0}
                    onClick={handleSendSms}
                    className="absolute right-2 top-1/2 -translate-y-1/2 px-3 py-1.5 text-xs text-daynote-accent border border-daynote-accent rounded-md disabled:opacity-40 bg-transparent cursor-pointer"
                  >
                    {smsCooldown > 0 ? `${smsCooldown}s` : t('auth.login.sendSms')}
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <label className="text-[11px] tracking-[0.1em] uppercase text-daynote-text/50 block mb-2">
                  {t('auth.login.emailLabel')}
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full bg-white/[0.04] border border-[var(--grid-line)] rounded-lg px-4 py-3.5 text-sm focus:outline-none focus:border-daynote-accent"
                />
              </div>
              <div>
                <label className="text-[11px] tracking-[0.1em] uppercase text-daynote-text/50 block mb-2">
                  {t('auth.login.passwordLabel')}
                </label>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="w-full bg-white/[0.04] border border-[var(--grid-line)] rounded-lg px-4 py-3.5 text-sm focus:outline-none focus:border-daynote-accent"
                />
              </div>
              <div className="flex justify-end">
                <Link to="/forgot-password" className="text-xs text-daynote-muted hover:text-daynote-accent">
                  {t('auth.login.forgotPassword')}
                </Link>
              </div>
            </div>
          )}

          <button
            type="button"
            disabled={loading}
            onClick={handleSubmit}
            className="w-full mt-7 py-4 bg-daynote-accent text-[#2a2520] rounded-lg font-semibold text-sm tracking-widest hover:brightness-110 transition-all disabled:opacity-60 border-none cursor-pointer"
          >
            {loading ? t('auth.login.submitting') : t('auth.login.submit')}
          </button>

          <div className="flex items-center gap-3.5 my-7 text-[11px] tracking-widest text-daynote-text/40">
            <div className="flex-1 h-px bg-[var(--grid-line)]" />
            {t('auth.login.or')}
            <div className="flex-1 h-px bg-[var(--grid-line)]" />
          </div>

          <div className="grid grid-cols-3 gap-2.5">
            <button
              type="button"
              onClick={handleWechatLogin}
              className="py-2.5 bg-white/[0.04] border border-[var(--grid-line)] rounded-lg text-xs text-daynote-muted hover:text-daynote-text hover:bg-white/[0.08] cursor-pointer"
            >
              {t('auth.login.wechat')}
            </button>
            <button
              type="button"
              disabled
              title="Coming soon"
              className="py-2.5 bg-white/[0.04] border border-[var(--grid-line)] rounded-lg text-xs text-daynote-text/30 cursor-not-allowed"
            >
              Apple
            </button>
            <button
              type="button"
              disabled
              title="Coming soon"
              className="py-2.5 bg-white/[0.04] border border-[var(--grid-line)] rounded-lg text-xs text-daynote-text/30 cursor-not-allowed"
            >
              Google
            </button>
          </div>

          <p className="text-[10px] text-daynote-text/40 text-center mt-7 leading-relaxed tracking-wide">
            {t('auth.login.termsPrefix')}{' '}
            <Link to="/terms" className="text-daynote-muted underline">
              {t('auth.login.termsLink')}
            </Link>{' '}
            {t('auth.login.termsAnd')}{' '}
            <Link to="/privacy" className="text-daynote-muted underline">
              {t('auth.login.privacyLink')}
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
